/**
 * Layout and feedback primitives, all ported from the frozen prototype: `.stack`, `.split`,
 * `.grid-2`, `.grid-3`, `.kpis`, `.toolbar`, `.tabs`, `.stepper`, `.progress`, `.summary-band`,
 * `.timeline`, `.copy-line`, `.url-box`, `.dropzone`, `.file-chip`, `.spinner`, `.code-block`.
 */
import {
  type DragEvent,
  type ReactElement,
  type ReactNode,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import { copyToClipboard } from '../lib/clipboard';
import { Button, IconButton } from './Button';
import { Icon } from './Icon';

export function Stack({ children, className }: { children: ReactNode; className?: string }): ReactElement {
  return <div className={['stack', className ?? ''].filter(Boolean).join(' ')}>{children}</div>;
}

export function Split({ children }: { children: ReactNode }): ReactElement {
  return <div className="split">{children}</div>;
}

export function Grid({ cols, children }: { cols: 2 | 3; children: ReactNode }): ReactElement {
  return <div className={cols === 2 ? 'grid-2' : 'grid-3'}>{children}</div>;
}

export function Kpis({ children }: { children: ReactNode }): ReactElement {
  return <div className="kpis">{children}</div>;
}

export function Toolbar({
  children,
  label,
}: {
  children: ReactNode;
  /** Accessible name for the filter cluster, e.g. "Kayıt filtreleri". */
  label: string;
}): ReactElement {
  return (
    // biome-ignore lint/a11y/noRedundantRoles: <search> is not mapped to its role by every engine and by jsdom, so the explicit role keeps the landmark discoverable
    <search className="toolbar" role="search" aria-label={label}>
      {children}
    </search>
  );
}

export function SearchInput({
  value,
  onChange,
  label,
  placeholder,
  onSubmit,
}: {
  value: string;
  onChange: (value: string) => void;
  label: string;
  placeholder?: string;
  onSubmit?: () => void;
}): ReactElement {
  const id = useId();
  return (
    <div className="field search" style={{ margin: 0 }}>
      <label className="sr-only" htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        className="input search"
        type="search"
        value={value}
        placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && onSubmit) {
            event.preventDefault();
            onSubmit();
          }
        }}
      />
    </div>
  );
}

export interface TabItem {
  key: string;
  label: ReactNode;
}

/** In-page tab strip (`.tabs`). Implemented as real tabs so arrow keys work. */
export function Tabs({
  items,
  active,
  onSelect,
  label,
}: {
  items: TabItem[];
  active: string;
  onSelect: (key: string) => void;
  label: string;
}): ReactElement {
  const baseId = useId();
  return (
    <div className="tabs" role="tablist" aria-label={label}>
      {items.map((item, index) => (
        <button
          type="button"
          key={item.key}
          id={`${baseId}-${item.key}`}
          role="tab"
          aria-selected={item.key === active}
          aria-controls={`${baseId}-${item.key}-panel`}
          tabIndex={item.key === active ? 0 : -1}
          className={['btn', 'btn-ghost', 'tab-btn', item.key === active ? 'is-active' : '']
            .filter(Boolean)
            .join(' ')}
          onClick={() => onSelect(item.key)}
          onKeyDown={(event) => {
            if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
            event.preventDefault();
            const step = event.key === 'ArrowRight' ? 1 : -1;
            const next = items[(index + step + items.length) % items.length];
            if (next) {
              onSelect(next.key);
              document.getElementById(`${baseId}-${next.key}`)?.focus();
            }
          }}
        >
          {item.label}
        </button>
      ))}
    </div>
  );
}

export function TabPanel({
  children,
  tabsId,
  tabKey,
}: {
  children: ReactNode;
  tabsId: string;
  tabKey: string;
}): ReactElement {
  return (
    <div role="tabpanel" id={`${tabsId}-${tabKey}-panel`} aria-labelledby={`${tabsId}-${tabKey}`}>
      {children}
    </div>
  );
}

export interface StepDescriptor {
  key: string;
  label: string;
}

/** Wizard progress (`.stepper`), used by batch issuance and the designer. */
export function Stepper({
  steps,
  currentIndex,
}: {
  steps: StepDescriptor[];
  currentIndex: number;
}): ReactElement {
  return (
    <ol
      className="stepper"
      aria-label={`Adım ${currentIndex + 1} / ${steps.length}`}
      style={{ listStyle: 'none', padding: 0, margin: '0 0 20px' }}
    >
      {steps.map((step, index) => (
        <li
          key={step.key}
          className={[
            'step',
            index === currentIndex ? 'is-current' : '',
            index < currentIndex ? 'is-done' : '',
          ]
            .filter(Boolean)
            .join(' ')}
          aria-current={index === currentIndex ? 'step' : undefined}
        >
          <span className="n" aria-hidden="true">
            {index < currentIndex ? '✓' : index + 1}
          </span>
          {step.label}
          {index < currentIndex ? <span className="sr-only">(tamamlandı)</span> : null}
        </li>
      ))}
    </ol>
  );
}

export function Progress({ value, max, label }: { value: number; max: number; label: string }): ReactElement {
  const pct = max > 0 ? Math.min(100, Math.max(0, Math.round((value / max) * 100))) : 0;
  return (
    <div
      className="progress"
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={max}
      aria-valuenow={value}
      aria-valuetext={`${value} / ${max}`}
      aria-label={label}
    >
      <i style={{ width: `${pct}%` }} />
    </div>
  );
}

export interface SummaryItem {
  key: string;
  value: ReactNode;
  label: string;
  tone?: 'ok' | 'bad' | 'dupe';
}

/** Batch counters (`.summary-band`). */
export function SummaryBand({ items }: { items: SummaryItem[] }): ReactElement {
  return (
    <div className="summary-band">
      {items.map((item) => (
        <div className={['item', item.tone ?? ''].filter(Boolean).join(' ')} key={item.key}>
          <b className="tnum">{item.value}</b>
          <span>{item.label}</span>
        </div>
      ))}
    </div>
  );
}

export interface TimelineEntry {
  key: string;
  tone: string;
  title: ReactNode;
  meta: ReactNode;
}

/** Audit trail (`.timeline`). */
export function Timeline({ entries }: { entries: TimelineEntry[] }): ReactElement {
  return (
    <ol className="timeline" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
      {entries.map((entry) => (
        <li className={['ev', entry.tone].filter(Boolean).join(' ')} key={entry.key}>
          <span className="dot" aria-hidden="true" />
          <b>{entry.title}</b>
          <span>{entry.meta}</span>
        </li>
      ))}
    </ol>
  );
}

/**
 * Copy control. Clipboard access can be refused by the browser, so the outcome is reported
 * either way and the value stays selectable as a fallback.
 */
export function CopyButton({
  value,
  label,
  onCopied,
}: {
  value: string;
  label: string;
  onCopied?: (ok: boolean) => void;
}): ReactElement {
  const [state, setState] = useState<'idle' | 'ok' | 'fail'>('idle');
  const timer = useRef<number | null>(null);

  useEffect(
    () => () => {
      if (timer.current !== null) window.clearTimeout(timer.current);
    },
    [],
  );

  const run = useCallback(async () => {
    const ok = await copyToClipboard(value);
    setState(ok ? 'ok' : 'fail');
    onCopied?.(ok);
    if (timer.current !== null) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setState('idle'), 2000);
  }, [onCopied, value]);

  return (
    <>
      <Button size="sm" variant="ghost" icon="copy" onClick={() => void run()}>
        {label}
      </Button>
      <span className="sr-only" role="status">
        {state === 'ok' ? 'Kopyalandı.' : state === 'fail' ? 'Kopyalanamadı, metni seçip kopyalayın.' : ''}
      </span>
    </>
  );
}

export function CopyLine({
  value,
  copyLabel = 'Kopyala',
  mono = true,
}: {
  value: string;
  copyLabel?: string;
  mono?: boolean;
}): ReactElement {
  return (
    <span className="copy-line">
      <span className={mono ? 'mono' : undefined}>{value}</span>
      <CopyButton value={value} label={copyLabel} />
    </span>
  );
}

/** URL row (`.url-box`) — public page and claim links. */
export function UrlBox({
  url,
  copyLabel = 'Kopyala',
  openLabel,
}: {
  url: string;
  copyLabel?: string;
  openLabel?: string;
}): ReactElement {
  return (
    <div className="url-box">
      <code>{url}</code>
      <CopyButton value={url} label={copyLabel} />
      {openLabel ? (
        <a className="btn btn-ghost btn-sm" href={url} target="_blank" rel="noopener noreferrer">
          {openLabel}
          <Icon name="ext" />
        </a>
      ) : null}
    </div>
  );
}

export function Spinner({ label }: { label?: string }): ReactElement {
  return (
    <span className="row-gap">
      <span className="spinner" aria-hidden="true" />
      {label ? <span className="small muted">{label}</span> : null}
    </span>
  );
}

/** Full-panel loading state; announced once so a slow request is not silent. */
export function Loading({ label = 'Yükleniyor…' }: { label?: string }): ReactElement {
  return (
    <div className="card-pad row-gap" role="status">
      <span className="spinner" aria-hidden="true" />
      <span className="small muted">{label}</span>
    </div>
  );
}

export function Skeleton({ lines = 3, title = false }: { lines?: number; title?: boolean }): ReactElement {
  return (
    <div className="skeleton-row" aria-hidden="true">
      {title ? <span className="skeleton skeleton-title" /> : null}
      {Array.from({ length: lines }, (_unused, index) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: fixed-length decorative placeholders, never reordered, no state to preserve
        <span className="skeleton skeleton-text" key={index} />
      ))}
    </div>
  );
}

export function EmptyState({
  title,
  children,
  action,
}: {
  title: ReactNode;
  children?: ReactNode;
  action?: ReactNode;
}): ReactElement {
  return (
    <div className="card-pad stack-tight" style={{ textAlign: 'center' }}>
      <b>{title}</b>
      {children ? <span className="small muted">{children}</span> : null}
      {action ? (
        <div className="row-gap" style={{ justifyContent: 'center' }}>
          {action}
        </div>
      ) : null}
    </div>
  );
}

/**
 * File picker (`.dropzone`). Drag-and-drop is an enhancement over a real `<input type="file">`,
 * so the control stays usable from the keyboard.
 */
export function Dropzone({
  accept,
  onFile,
  hint,
  disabled = false,
  label,
}: {
  accept: string;
  onFile: (file: File) => void;
  hint: ReactNode;
  disabled?: boolean;
  label: string;
}): ReactElement {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [over, setOver] = useState(false);
  const id = useId();

  function handleDrop(event: DragEvent<HTMLDivElement>): void {
    event.preventDefault();
    setOver(false);
    if (disabled) return;
    const file = event.dataTransfer.files.item(0);
    if (file) onFile(file);
  }

  return (
    // Drag-and-drop is an enhancement on a non-interactive surface: the accessible control is the labelled file input inside, so this element takes no role and no focus.
    // biome-ignore lint/a11y/noStaticElementInteractions: drop target duplicates the labelled file input below it
    <div
      className={['dropzone', over ? 'is-over' : ''].filter(Boolean).join(' ')}
      onDragOver={(event) => {
        event.preventDefault();
        if (!disabled) setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={handleDrop}
      role="presentation"
    >
      <Icon name="upload" aria-hidden="true" />
      <b>{label}</b>
      <span className="small muted">{hint}</span>
      <label className="btn btn-secondary btn-sm" htmlFor={id}>
        Dosya seç
      </label>
      <input
        ref={inputRef}
        id={id}
        type="file"
        accept={accept}
        disabled={disabled}
        className="sr-only"
        onChange={(event) => {
          const file = event.target.files?.item(0);
          if (file) onFile(file);
          event.target.value = '';
        }}
      />
    </div>
  );
}

export function FileChip({
  name,
  size,
  onRemove,
}: {
  name: string;
  size?: string;
  onRemove?: () => void;
}): ReactElement {
  return (
    <span className="file-chip">
      <Icon name="check" aria-hidden="true" />
      <span>{name}</span>
      {size ? <span className="muted small">{size}</span> : null}
      {onRemove ? (
        <IconButton icon="x" label={`${name} dosyasını kaldır`} size="xs" onClick={onRemove} />
      ) : null}
    </span>
  );
}

/** Pre-formatted technical block (`.code-block`) — manifests, CSV previews, helper payloads. */
export function CodeBlock({ children, label }: { children: string; label?: string }): ReactElement {
  return (
    <div className="stack-tight">
      {label ? <span className="help">{label}</span> : null}
      <pre className="code-block">{children}</pre>
    </div>
  );
}
